"use client"

import * as React from "react"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { Select as SelectPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function Select({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root> & {
  className?: string
}) {
  return (
    <SelectPrimitive.Root {...props}>
      <div data-slot="select" className={cn(className)}>
        {children}
      </div>
    </SelectPrimitive.Root>
  )
}

function SelectGroup({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("scroll-my-1", className)}
      {...props}
    />
  )
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default"
}) {
  const isTradeDetailsJournalControl = "data-trade-details-journal-control" in props
  const isTradingActivityCalendarControl =
    "data-trading-activity-calendar-control" in props
  const isTradeLogToolbarFieldSelect = "data-trade-log-toolbar-field-select" in props
  const isReportsFilterSelect = "data-reports-filter-select" in props
  const isAccountSettingsFieldSelect = "data-account-settings-field-select" in props
  const isSolidSurfaceSelectControl =
    isTradeDetailsJournalControl ||
    isTradingActivityCalendarControl ||
    isTradeLogToolbarFieldSelect ||
    isReportsFilterSelect ||
    isAccountSettingsFieldSelect

  const filterToolbarFieldSelectTextClass =
    "text-gray-500 dark:text-sidebar-muted-foreground hover:!text-gray-500 dark:hover:!text-sidebar-muted-foreground data-[state=open]:!text-gray-500 dark:data-[state=open]:!text-sidebar-muted-foreground focus-visible:!text-gray-500 dark:focus-visible:!text-sidebar-muted-foreground [&_[data-slot=select-value]]:!text-gray-500 dark:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground hover:[&_[data-slot=select-value]]:!text-gray-500 dark:hover:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground data-[state=open]:[&_[data-slot=select-value]]:!text-gray-500 dark:data-[state=open]:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground focus-visible:[&_[data-slot=select-value]]:!text-gray-500 dark:focus-visible:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground [&_svg]:!text-gray-500 dark:[&_svg]:!text-sidebar-muted-foreground hover:[&_svg]:!text-gray-500 dark:hover:[&_svg]:!text-sidebar-muted-foreground data-[state=open]:[&_svg]:!text-gray-500 dark:data-[state=open]:[&_svg]:!text-sidebar-muted-foreground focus-visible:[&_svg]:!text-gray-500 dark:focus-visible:[&_svg]:!text-sidebar-muted-foreground"

  const accountSettingsFieldSelectTextClass =
    "text-gray-500 dark:text-white hover:!text-gray-500 dark:hover:!text-white data-[state=open]:!text-gray-500 dark:data-[state=open]:!text-white focus-visible:!text-gray-500 dark:focus-visible:!text-white [&_[data-slot=select-value]]:!text-gray-500 dark:[&_[data-slot=select-value]]:!text-white hover:[&_[data-slot=select-value]]:!text-gray-500 dark:hover:[&_[data-slot=select-value]]:!text-white data-[state=open]:[&_[data-slot=select-value]]:!text-gray-500 dark:data-[state=open]:[&_[data-slot=select-value]]:!text-white focus-visible:[&_[data-slot=select-value]]:!text-gray-500 dark:focus-visible:[&_[data-slot=select-value]]:!text-white [&_svg]:!text-gray-500 dark:[&_svg]:!text-white hover:[&_svg]:!text-gray-500 dark:hover:[&_svg]:!text-white data-[state=open]:[&_svg]:!text-gray-500 dark:data-[state=open]:[&_svg]:!text-white focus-visible:[&_svg]:!text-gray-500 dark:focus-visible:[&_svg]:!text-white"

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-fit items-center justify-between gap-1.5 rounded-[6px] border border-input py-2 pr-2 pl-2.5 text-sm whitespace-nowrap outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-placeholder:text-muted-foreground data-[size=default]:h-8 data-[size=sm]:h-7 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        !isSolidSurfaceSelectControl && "[&_svg]:text-muted-foreground",
        !isSolidSurfaceSelectControl && "transition-colors",
        !isSolidSurfaceSelectControl && "bg-transparent dark:bg-input/30 dark:hover:bg-input/50",
        isTradeDetailsJournalControl && "dark:bg-sidebar dark:hover:bg-sidebar",
        isTradingActivityCalendarControl &&
          "bg-card dark:bg-sidebar dark:hover:bg-sidebar dark:focus-visible:bg-sidebar",
        isTradeLogToolbarFieldSelect && filterToolbarFieldSelectTextClass,
        isReportsFilterSelect && filterToolbarFieldSelectTextClass,
        isAccountSettingsFieldSelect && accountSettingsFieldSelectTextClass,
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon
          className={cn(
            "pointer-events-none size-4",
            !isTradeLogToolbarFieldSelect &&
              !isReportsFilterSelect &&
              !isAccountSettingsFieldSelect &&
              "text-muted-foreground"
          )}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "center",
  fitToContent = false,
  portalContainer,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  fitToContent?: boolean
  portalContainer?: HTMLElement | null
}) {
  const isAccountSettingsFieldSelectContent =
    "data-account-settings-field-select-content" in props
  const isFilterSelectContent =
    "data-trade-log-filter-select-content" in props ||
    "data-reports-filter-select-content" in props ||
    "data-card-filter-select-content" in props ||
    isAccountSettingsFieldSelectContent

  return (
    <SelectPrimitive.Portal container={portalContainer ?? undefined}>
      <SelectPrimitive.Content
        data-slot="select-content"
        data-select-position={position}
        data-align-trigger={position === "item-aligned"}
        data-fit-select-content={fitToContent ? "" : undefined}
        className={cn(
          "relative z-50 max-h-(--radix-select-content-available-height) min-w-36 origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-[6px] border border-input bg-popover shadow-none duration-100 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          isAccountSettingsFieldSelectContent
            ? "text-gray-500 dark:text-white"
            : isFilterSelectContent
            ? "text-gray-500 dark:text-sidebar-muted-foreground"
            : "text-popover-foreground",
          position === "popper" &&
            (fitToContent
              ? "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1"
              : "w-(--radix-select-trigger-width) min-w-(--radix-select-trigger-width) max-w-(--radix-select-trigger-width) data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1"),
          fitToContent && "overflow-x-visible",
          className
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          data-slot="select-viewport"
          className={cn(
            "p-1",
            position === "popper" &&
              (fitToContent
                ? "h-auto w-full min-w-full max-h-(--radix-select-content-available-height) scroll-my-1"
                : "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1")
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("px-1.5 py-1 text-xs text-muted-foreground", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default items-start gap-1.5 rounded-[4px] py-1.5 pr-8 pl-1.5 text-sm text-gray-500 outline-hidden select-none focus:bg-accent focus:text-gray-500 data-highlighted:bg-accent data-highlighted:text-gray-500 not-data-[variant=destructive]:focus:**:text-gray-500 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="pointer-events-none" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "z-10 flex cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "z-10 flex cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}